// How It Works — FAQ (v2, Jul 2026, Randy's full refresh).
//
// A ground-up rewrite of the old FAQ tab. Every answer here reflects the CURRENT
// product: the Workbench (comparison) + File Refinery (Big-File tool), the
// à-la-carte Creator Pro toolkit (no more single "$300 affiliate gate"), tips /
// subscriptions / Partner referral credit (FTC-safe, disclaimered), the three
// tiers, the public silo, privacy/termination, and the live model glossary.
//
// Search bar pinned at the top (Randy's request) — filters every Q&A live.
import { useMemo, useState } from "react";
import {
  MagnifyingGlass, CaretDown, CaretRight, Sparkle, Stack, Wrench, YoutubeLogo,
  Globe, CurrencyDollar, Crown, ShieldCheck, Brain, Lightning, X,
} from "@phosphor-icons/react";
import EarningsDisclaimer from "@/components/EarningsDisclaimer";
import { modelsByProvider } from "@/lib/modelGlossary";

// ---------------------------------------------------------------------------
// The FAQ corpus. Each category is a titled group of {q, a[]} items. `a` is an
// array of paragraphs. `note` renders an inline callout under the category head.
// ---------------------------------------------------------------------------
const CATEGORIES = [
  {
    id: "basics", title: "The basics", icon: Sparkle,
    items: [
      { q: "What is My Silo?", a: [
        { h: "Your AI. Your Knowledge. Your Business." },
        "My Silo is a platform that lets anyone create a personal Digital Twin — an AI version of themselves, built from their own knowledge, content, experience, and perspective.",
        "Today's AI tools are powerful, but they're designed to serve everyone. As a result, their answers are often broad, generic, and pulled from across the internet. Sometimes that's useful. Other times, you want expertise from one specific source you trust. My Silo solves that.",
        "Picture a YouTube creator whose audience asks the same questions every day. Instead of losing hours in the comments, they deploy a Digital Twin that answers in their voice, references only their content, promotes only their products and affiliate links, and points people back to their videos. For coaches, influencers, consultants, hobby experts, educators, and business owners, My Silo becomes a 24/7 extension of themselves.",
        { h: "What makes My Silo different?" },
        { ul: [
          "Your data stays yours.",
          "Your AI answers from your knowledge only.",
          "Your products, links, and recommendations are prioritized.",
          "Your audience can reach your expertise anytime.",
        ]},
        "Think of it as a private, curated knowledge vault that you choose to share with the world.",
        { h: "Monetize your expertise" },
        "Creators can offer their Digital Twin free, charge a monthly subscription, build a community around their expertise, and generate recurring income from their knowledge. People can discover silos, subscribe to experts they trust, and get on-demand access to specialized knowledge in almost any niche.",
        { h: "Built for growth" },
        "Beyond Digital Twins, My Silo includes:",
        { ul: [
          "Advanced AI-powered knowledge-management tools,",
          "Content-curation and corpus-building workbenches,",
          "Referral and affiliate growth systems,",
          "Social challenges where silos compare knowledge and expertise,",
          "Partner opportunities for people who want to promote and profit from the platform.",
        ]},
        "In a world increasingly dominated by AI-generated noise, My Silo gives people a way to preserve, protect, monetize, and scale what's uniquely theirs — their knowledge, their voice, and their expertise. Your knowledge deserves its own home. Welcome to My Silo.",
      ]},
      { q: "What exactly is a “silo”?", a: [
        "A silo is a sovereign, isolated AI twin: its own library, its own voice, its own public link. Silos never bleed into one another — one silo can’t see another’s material, even if you own both.",
      ]},
      { q: "How do I build one?", a: [
        "Create a silo, then feed its Library — upload documents, paste text, or pull in YouTube transcripts. Organize that material into three tiers (Anchor, Logic, Library), shape the voice, and publish the public Q&A page. No coding, ever.",
      ]},
      { q: "Do I need to be technical?", a: [
        "No. If you can upload a file or paste text, you can build a silo. The platform handles the retrieval and answering for you.",
      ]},
      { q: "How many silos can I create?", a: [
        "The Creator tier ({{platform_fee}}/mo) lets you build up to {{creator_silo_cap}} branded silos. Creator Pro members unlock up to {{creator_pro_silo_cap}} silos, plus the full building toolkit (Boost, Macros, Collaboration, Silo Wars, YouTube Digital Twin Kit).",
        "If you legitimately need more than {{creator_pro_silo_cap}} silos, reach out to support and we'll evaluate the case.",
      ]},
    ],
  },
  {
    id: "tiers", title: "The three tiers (how your twin “knows” you)", icon: Stack,
    items: [
      { q: "What are Tier 1, Tier 2, and Tier 3?", a: [
        "Tier 1 — Anchor: your core identity and voice. It’s always in context, so every answer sounds like you.",
        "Tier 2 — Logic: your reasoning, frameworks, and positions. Smart-searched when a question calls for it.",
        "Tier 3 — Library: your full archive. Retrieved on demand, passage by passage.",
        "Answers are grounded in these tiers through retrieval — the twin quotes your material rather than making things up.",
      ]},
      { q: "Why tiers instead of one big pile of documents?", a: [
        "Focus and cost. Keeping Tier 1 always present makes the voice consistent; searching the deeper tiers only when relevant keeps answers sharp and your bill low. Dumping everything into every answer would be slower, pricier, and vaguer.",
      ]},
    ],
  },
  {
    id: "tools", title: "The two building tools", icon: Wrench,
    items: [
      { q: "Workbench vs. File Refinery — which do I use?", a: [
        "The Workbench is the comparison tool. You hit Run and it hands you back one answer on the spot. Its superpower is relating things across its slots — comparing drafts, merging chapters, cross-referencing sources. Anything that needs to see several files at once lives here.",
        "The File Refinery is the Big-File tool. You start it and walk away. It’s built to chew through one enormous file of any size — Accurately Condense a whole book, Summarize a giant transcript — reliably, in the background.",
        "Simplest way to say it: the Workbench relates a few things together, live. The Refinery grinds through one huge thing, dependably, in the background.",
      ]},
      { q: "If I Boost the Workbench on a really large file, will it fail?", a: [
        "Partly. The Workbench sends your whole file to the AI in one gulp, so its ceiling is how much the AI can hold at once. If the file is bigger than that (even boosted), the Workbench stops you with a “too big” message. And one gulp is all-or-nothing — a mid-way hiccup loses the run.",
        "For a genuinely huge file, use the File Refinery. It takes bites instead of one gulp, so it sails past any single model’s ceiling and, if a bite hiccups, it retries or skips just that bite.",
      ]},
      { q: "Why can’t the Workbench be as dependable as the Refinery?", a: [
        "Because the Refinery’s bite-by-bite trick only works when each bite can stand alone. For a single-file job (like Condense) that’s fine. For a comparison job it isn’t — the connection you’re hunting for might live between page 2 of file A and page 40 of file B, so the AI has to see everything at once. Chunking and comparing are fundamentally incompatible.",
        "That’s why every single-file operation now lives in the File Refinery too — Accurately Condense, Summarize (Retain 100%), and the rest. Comparison operations stay in the Workbench.",
      ]},
      { q: "Does Boost change the File Refinery’s size limit?", a: [
        "Yes — Boost blows right past it. Normally the Refinery slices a file into ~8,000-character bites. Turn Boost on (with a big-context model) and it raises that dramatically — roughly 800,000 characters per piece on Gemini, ~200,000 on Claude/GPT — effectively one single pass for most documents.",
      ]},
      { q: "What are Macros?", a: [
        "A Macro records a workflow once — pick an operation, set the output size, choose where the result should land, Run — and replays it on the next file with one click. Both the Workbench and the File Refinery have their own Macros; the Refinery’s wait for the background job to finish before moving on, and warn you if any chunk was skipped.",
      ]},
    ],
  },
  {
    id: "material", title: "Pulling in your material", icon: YoutubeLogo,
    items: [
      { q: "What can I put into a silo?", a: [
        "Documents and pasted text, plus YouTube — a video’s title, description, transcript, and comments — through the built-in YouTube tool.",
      ]},
      { q: "What’s the YouTube tool?", a: [
        "It pulls a video’s transcript, description, or comments straight into a Workbench/Refinery slot or your Library — so your twin can speak from your own videos without you copy-pasting anything.",
      ]},
    ],
  },
  {
    id: "public", title: "Your public silo", icon: Globe,
    items: [
      { q: "What do fans actually see?", a: [
        "Your public Q&A page, at my-silo…/s/your-slug. Visitors ask questions and your twin answers in your voice, grounded in your material.",
      ]},
      { q: "Free, subscriber, or premium — who decides?", a: [
        "You do. A public silo is free to ask. You can gate deeper content or richer answers behind a subscription you price yourself.",
      ]},
      { q: "Can my silo speak its answers?", a: [
        "Yes — silos can read answers aloud where voice is enabled, so your twin can literally sound like you.",
      ]},
      { q: "Can I hide a silo from the public?", a: [
        "Yes. Toggle “Hide” on the dashboard card. The public link 404s for the world while you keep managing it privately. Flip it back any time.",
      ]},
    ],
  },
  {
    id: "money", title: "Making money", icon: CurrencyDollar, disclaimer: true,
    items: [
      { q: "How can a creator earn on My Silo?", a: [
        "Three product paths: (1) tips on public silos, (2) paid subscriptions you price yourself, and (3) the Partner Program — referral credit for people you bring who become paying customers.",
        "What you earn depends entirely on your own effort, audience, and pricing. There are no guarantees of income.",
      ]},
      { q: "How do tips work?", a: [
        "Any public silo with the tip badge enabled lets a fan send a one-time tip. 100% of the tip amount reaches you — the supporter pays a small My Silo service fee on top, so nothing is deducted from what they sent you. Tips are one-time, not recurring.",
      ]},
      { q: "How do subscriptions work?", a: [
        "You set a monthly price for premium access. Fans subscribe through Stripe. You keep your subscriber revenue — the platform charges you its own flat Creator Pro fee, not a slice of your subscriptions.",
      ]},
      { q: "What is the Partner Program (referral credit)?", a: [
        "When someone signs up through your invite link and later becomes a paying customer, that referral locks to you, and you earn referral credit while they remain a paying customer.",
        "It is single-level only — you never earn from referrals-of-referrals (a strict no-MLM rule). Your live rates, your referral dashboard, and every edge-case (cancellations, payouts, tax) are in your Earnings tab.",
      ]},
      { q: "How do I get paid?", a: [
        "Payouts run through Stripe Connect (full cash-payout support is rolling out). Connect your Stripe account under Account & Billing so earnings have somewhere to land.",
      ]},
      { q: "What happens to my referrals if I cancel or leave?", a: [
        "Referral locks you’ve already earned stay with you, and already-paid credit is never clawed back. The complete set of exit scenarios (cancel, delete, inactivity, bans, beneficiary) lives in your Earnings tab and the account policy.",
      ]},
    ],
  },
  {
    id: "creatorpro", title: "Creator Pro (the toolkit)", icon: Crown,
    items: [
      { q: "What is Creator Pro?", a: [
        "An à-la-carte toolkit of advanced building tools. You start from the Minimum Workbench base and add exactly the tools you want — Boost, output-size control, the AI Assistant, the YouTube twin, Macros, the File Refinery, Silo Wars, collaborator management, and more.",
      ]},
      { q: "Do I need Creator Pro to use My Silo?", a: [
        "No. You can build, publish, and monetize a silo without it. Creator Pro unlocks the power tools that let you build faster and handle bigger material.",
      ]},
      { q: "How is it priced?", a: [
        "Pick-what-you-want: a base workbench fee plus per-tool add-ons. Open the upgrade buffet from any locked tool (or your Earnings tab) to see the exact current prices — you only pay for the pieces you switch on.",
      ]},
    ],
  },
  {
    id: "privacy", title: "Privacy, safety & your account", icon: ShieldCheck,
    items: [
      { q: "Are my silos private from each other?", a: [
        "Completely. Each silo is fully isolated — one silo’s material is never visible to another, and never used to answer another silo’s questions.",
      ]},
      { q: "How do I report a silo?", a: [
        "Every public silo has a discreet “Report this silo” button at the bottom of the page. Describe the issue and it goes straight to the platform admin.",
      ]},
      { q: "What happens if I delete my account?", a: [
        "Voluntary deletion has a 30-day grace window — sign back in within 30 days to undo it. Involuntary termination triggers (abuse, fraud, illegal content, KYC failure, long-term inactivity) are enumerated up front, no surprises.",
      ], link: { href: "/policy/account-termination", label: "Read the full account-termination policy \u2192" }},
    ],
  },
  {
    id: "vs", title: "Why not just use Gemini or ChatGPT directly?", icon: Lightning,
    items: [
      { q: "What does My Silo add over pasting into a public chatbot?", a: [
        "Grounding: your twin answers from YOUR material and can show where an answer came from, instead of hand-waving.",
        "No “lost in the middle”: on huge inputs, general chatbots skim the middle. The File Refinery works in small, focused bites so nothing gets skimmed.",
        "It accumulates instead of compressing: a chatbot can only write back a few thousand characters per reply; the Refinery adds bite-by-bite, so the result can be as long as it needs to be.",
        "It’s durable and structured: retries on hiccups, saves progress, and drops results straight into your silo — tagged and reusable.",
        "Short version: a public chatbot is a clever friend; My Silo is an assembly line. A friend answers a quick question; an assembly line builds a car.",
      ]},
    ],
  },
];

// Flat list for search.
function flatten() {
  const out = [];
  for (const cat of CATEGORIES) {
    for (const it of cat.items) {
      out.push({ ...it, catId: cat.id, catTitle: cat.title });
    }
  }
  return out;
}

// Answer entries can be a string (paragraph), {h} (heading), or {ul} (bullets).
function answerText(a) {
  return a.map((b) => (typeof b === "string" ? b : b.h || (b.ul ? b.ul.join(" ") : ""))).join(" ");
}

function AnswerBlock({ item }) {
  return (
    <div className="text-sm text-[#4A4A47] leading-relaxed space-y-2.5" data-testid="faq2-answer">
      {item.a.map((blk, i) => {
        if (typeof blk === "string") return <p key={i}>{blk}</p>;
        if (blk.h) return <div key={i} className="font-serif-display text-[15px] text-[#1C1B1A] pt-1">{blk.h}</div>;
        if (blk.ul) return (
          <ul key={i} className="list-disc pl-5 space-y-1 marker:text-[#4A5D4E]">
            {blk.ul.map((li, j) => <li key={j}>{li}</li>)}
          </ul>
        );
        return null;
      })}
      {item.link && (
        <a href={item.link.href} target="_blank" rel="noopener noreferrer"
          className="inline-block text-[#4A5D4E] underline decoration-dotted underline-offset-2 hover:decoration-solid" data-testid="faq2-policy-link">
          {item.link.label}
        </a>
      )}
    </div>
  );
}

function QAItem({ item, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b hairline last:border-b-0" data-testid="faq2-qa">
      <button onClick={() => setOpen((o) => !o)} data-testid="faq2-qa-toggle"
        className="w-full flex items-start gap-3 text-left py-3.5 group">
        {open ? <CaretDown size={16} className="mt-0.5 shrink-0 text-[#4A5D4E]" /> : <CaretRight size={16} className="mt-0.5 shrink-0 text-[#9A9890] group-hover:text-[#4A5D4E]" />}
        <span className="flex-1 text-[15px] text-[#1C1B1A] font-medium">{item.q}</span>
      </button>
      {open && <div className="pl-7 pb-4 -mt-1"><AnswerBlock item={item} /></div>}
    </div>
  );
}

function ModelsSection() {
  const byProvider = useMemo(() => modelsByProvider(), []);
  return (
    <div data-testid="faq2-models">
      <p className="text-sm text-[#4A4A47] mb-4 leading-relaxed">
        Every silo runs on a real large-language model under the hood. Here’s the current lineup and what each is good at — you (or the platform) can point different surfaces at different models.
      </p>
      <div className="space-y-5">
        {Object.entries(byProvider).map(([provider, models]) => (
          <div key={provider}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5D4E] mb-2">{provider}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {models.map((m) => (
                <div key={m.id} className="bg-[#FDFCF8] border hairline rounded-lg p-3.5" data-testid={`faq2-model-${m.id}`}>
                  <div className="font-medium text-[#1C1B1A] text-sm">{m.label}</div>
                  {m.strengths && <div className="text-xs text-[#6B6A68] mt-1 leading-relaxed">{m.strengths}</div>}
                  {m.costs && <div className="text-[11px] text-[#9A8B5A] mt-1.5 tabular-nums">{m.costs}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HowItWorksFAQv2() {
  const [query, setQuery] = useState("");
  const [openCat, setOpenCat] = useState("basics");
  const q = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!q) return null;
    return flatten().filter((it) => {
      const hay = (it.q + " " + answerText(it.a) + " " + it.catTitle).toLowerCase();
      return q.split(/\s+/).every((tok) => hay.includes(tok));
    });
  }, [q]);

  return (
    <div className="max-w-3xl" data-testid="faq2-root">
      <div className="mb-6">
        <h2 className="font-serif-display text-3xl text-[#1C1B1A]">How it works — the fresh FAQ</h2>
        <p className="text-sm text-[#6B6A68] mt-2 max-w-2xl">
          Everything about My Silo, rewritten and current. Search it, or browse by topic below.
        </p>
      </div>

      {/* Search — pinned at the top per Randy. */}
      <div className="sticky top-14 z-20 bg-[#FDFCF8]/95 backdrop-blur-sm py-2 -mx-1 px-1 mb-4">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9A9890]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the FAQ — e.g. “tips”, “big file”, “get paid”…"
            data-testid="faq2-search"
            className="w-full bg-white border hairline rounded-xl pl-11 pr-10 py-3 text-sm text-[#1C1B1A] focus:outline-none focus:ring-2 focus:ring-[#4A5D4E]/30 focus:border-[#4A5D4E]"
          />
          {query && (
            <button onClick={() => setQuery("")} data-testid="faq2-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9A9890] hover:text-[#C16E5A]">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Search results */}
      {results !== null ? (
        <div data-testid="faq2-search-results">
          <div className="text-xs text-[#6B6A68] mb-3">
            {results.length} result{results.length === 1 ? "" : "s"} for “{query}”
          </div>
          {results.length === 0 ? (
            <div className="text-sm text-[#6B6A68] italic bg-white border hairline rounded-xl p-6 text-center" data-testid="faq2-no-results">
              Nothing matched. Try a simpler word — or clear the search to browse everything.
            </div>
          ) : (
            <div className="bg-white border hairline rounded-xl px-4">
              {results.map((it, i) => (
                <div key={i}>
                  <div className="pt-3 text-[10px] uppercase tracking-[0.18em] text-[#9A8B5A]">{it.catTitle}</div>
                  <QAItem item={it} defaultOpen />
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        // Browse by category
        <div className="space-y-3" data-testid="faq2-categories">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isOpen = openCat === cat.id;
            return (
              <div key={cat.id} className="bg-white border hairline rounded-xl overflow-hidden" data-testid={`faq2-cat-${cat.id}`}>
                <button onClick={() => setOpenCat(isOpen ? null : cat.id)} data-testid={`faq2-cat-toggle-${cat.id}`}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#FDFCF8] transition-colors">
                  <span className="w-9 h-9 rounded-lg bg-[#EEF1EC] text-[#4A5D4E] flex items-center justify-center shrink-0">
                    <Icon size={18} weight="duotone" />
                  </span>
                  <span className="flex-1 font-serif-display text-lg text-[#1C1B1A]">{cat.title}</span>
                  <span className="text-xs text-[#9A9890] tabular-nums mr-1">{cat.items.length}</span>
                  {isOpen ? <CaretDown size={18} className="text-[#4A5D4E]" /> : <CaretRight size={18} className="text-[#9A9890]" />}
                </button>
                {isOpen && (
                  <div className="px-5 pb-2 border-t hairline">
                    {cat.disclaimer && (
                      <div className="pt-3" data-testid="faq2-earnings-disclaimer">
                        <EarningsDisclaimer variant="short" />
                      </div>
                    )}
                    {cat.items.map((it, i) => <QAItem key={i} item={it} />)}
                  </div>
                )}
              </div>
            );
          })}

          {/* Models glossary — always at the bottom, expandable */}
          <div className="bg-white border hairline rounded-xl overflow-hidden" data-testid="faq2-cat-models">
            <button onClick={() => setOpenCat(openCat === "models" ? null : "models")} data-testid="faq2-cat-toggle-models"
              className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#FDFCF8] transition-colors">
              <span className="w-9 h-9 rounded-lg bg-[#EEF1EC] text-[#4A5D4E] flex items-center justify-center shrink-0">
                <Brain size={18} weight="duotone" />
              </span>
              <span className="flex-1 font-serif-display text-lg text-[#1C1B1A]">The AI models</span>
              {openCat === "models" ? <CaretDown size={18} className="text-[#4A5D4E]" /> : <CaretRight size={18} className="text-[#9A9890]" />}
            </button>
            {openCat === "models" && <div className="px-5 py-4 border-t hairline"><ModelsSection /></div>}
          </div>
        </div>
      )}
    </div>
  );
}
